/**
 * /erp/settings/drivers — manage delivery drivers (Manager+).
 *
 * Single-page CRUD: top create form + list with inline rename /
 * phone+license edit / activate-toggle / delete. Mirrors the
 * warehouses page exactly (same patterns, same toast / confirm /
 * mutation flow). Drivers feed the Dispatch picker in PR-F.
 *
 * Phone + license are optional — Q10 from the PRD allows them empty.
 * License has no shape validation in v1 (formats vary wildly across
 * regions).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  ApiError,
  type ErpDriver,
  type ErpDriverCreate,
  type ErpDriverUpdate,
  erpApi,
  isErpDispatchMasterConflict,
} from "../lib/api";
import { ERP_QUERY_KEYS, useDrivers } from "../lib/erp";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/erp/settings/drivers")({
  beforeLoad: () => {
    throw redirect({ to: "/settings/drivers" });
  },
  component: DriversAdminPage,
});

export function DriversAdminPage() {
  const queryClient = useQueryClient();
  const driversQ = useDrivers();
  const drivers = driversQ.data?.items ?? [];

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.drivers });
  };

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <UserIcon className="size-5 opacity-70" aria-hidden />
          Drivers
        </h2>
        <p className="text-sm opacity-60">
          People who actually drive a Dispatch. Codes auto-generate as DRV-NNNNN — override on
          create if you'd rather use something descriptive (e.g. <code>OWN-DRV</code>). Deactivate
          when a driver leaves; rows are kept for historical dispatch records.
        </p>
      </header>

      <CreateDriverBar onCreated={onChanged} />

      {driversQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : driversQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load drivers: {(driversQ.error as Error).message}
        </p>
      ) : drivers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <UserIcon className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">No drivers yet.</p>
          <p className="mt-1 text-sm opacity-60">Add one above to get started.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {drivers.map((d) => (
            <DriverRow key={d.id} driver={d} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateDriverBar({ onCreated }: { onCreated: () => void }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [license, setLicense] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: ErpDriverCreate) => erpApi.drivers.create(body),
    onSuccess: () => {
      setCode("");
      setName("");
      setPhone("");
      setLicense("");
      setError(null);
      onCreated();
      toast.success("Driver created.");
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        isErpDispatchMasterConflict(err.detail)
      ) {
        setError(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not create driver.";
      setError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    const body: ErpDriverCreate = { name: name.trim() };
    if (code.trim()) body.code = code.trim();
    if (phone.trim()) body.phone = phone.trim();
    if (license.trim()) body.license = license.trim();
    createMutation.mutate(body);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-[var(--border)] p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={32}
            placeholder="auto"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">
            Name <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Phone</span>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={50}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">License</span>
          <input
            type="text"
            value={license}
            onChange={(e) => setLicense(e.target.value)}
            maxLength={100}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={createMutation.isPending || !name.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="size-4" aria-hidden />
          )}
          Add driver
        </button>
      </div>
    </form>
  );
}

function DriverRow({
  driver,
  onChanged,
}: {
  driver: ErpDriver;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(driver.name);
  const [phone, setPhone] = useState(driver.phone ?? "");
  const [license, setLicense] = useState(driver.license ?? "");

  const updateMutation = useMutation({
    mutationFn: (body: ErpDriverUpdate) => erpApi.drivers.update(driver.id, body),
    onSuccess: () => {
      onChanged();
      setEditing(false);
      toast.success("Saved.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not save.";
      toast.error(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => erpApi.drivers.remove(driver.id),
    onSuccess: () => {
      onChanged();
      toast.success("Driver deleted.");
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        isErpDispatchMasterConflict(err.detail)
      ) {
        toast.error(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
    },
  });

  const startEdit = () => {
    setName(driver.name);
    setPhone(driver.phone ?? "");
    setLicense(driver.license ?? "");
    setEditing(true);
  };

  const submitEdit = () => {
    const body: ErpDriverUpdate = {};
    if (name.trim() !== driver.name) body.name = name.trim();
    const newPhone = phone.trim() || null;
    if (newPhone !== (driver.phone ?? null)) body.phone = newPhone;
    const newLicense = license.trim() || null;
    if (newLicense !== (driver.license ?? null)) body.license = newLicense;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(body);
  };

  const toggleActive = () => {
    updateMutation.mutate({ is_active: !driver.is_active });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete driver ${driver.code}? This cannot be undone.`)) {
      removeMutation.mutate();
    }
  };

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editing ? (
          <div className="flex flex-wrap items-start gap-2">
            <span className="rounded-md bg-black/5 px-2 py-1 font-mono text-xs dark:bg-white/10">
              {driver.code}
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Name"
            />
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={50}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Phone"
            />
            <input
              type="text"
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              maxLength={100}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="License"
            />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-black/5 px-2 py-0.5 font-mono text-xs dark:bg-white/10">
              {driver.code}
            </span>
            <span className="font-medium">{driver.name}</span>
            {driver.phone && <span className="text-xs opacity-60">{driver.phone}</span>}
            {driver.license && (
              <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] opacity-70 dark:bg-white/10">
                {driver.license}
              </span>
            )}
            {!driver.is_active && (
              <span className="rounded-full bg-black/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider opacity-60 dark:bg-white/10">
                Inactive
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {editing ? (
          <>
            <button
              type="button"
              onClick={submitEdit}
              disabled={updateMutation.isPending}
              aria-label="Save"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              {updateMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Save className="size-3" aria-hidden />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              aria-label="Cancel"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <X className="size-3" aria-hidden />
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={toggleActive}
              disabled={updateMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              {driver.is_active ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              onClick={startEdit}
              aria-label={`Edit ${driver.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Pencil className="size-3" aria-hidden />
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={removeMutation.isPending}
              aria-label={`Delete ${driver.code}`}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-3 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3" aria-hidden />
              )}
              Delete
            </button>
          </>
        )}
      </div>
    </li>
  );
}
